// Search Engine Utility Functions

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @param {string} format - Format type (short, medium, long, time, datetime)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'long') => {
  if (!date) return ''

  const d = new Date(date)

  switch (format) {
    case 'short':
      return d.toLocaleDateString('en-ZA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    case 'medium':
      return d.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    case 'long':
      return d.toLocaleDateString('en-ZA', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    case 'time':
      return d.toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    case 'datetime':
      return d.toLocaleString('en-ZA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    default:
      return d.toLocaleDateString('en-ZA')
  }
}

/**
 * Format time for display
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${displayHour}:${minutes} ${ampm}`
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371 // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees to convert
 * @returns {number} Radians
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180)
}

/**
 * Format distance for display
 * @param {number} distance - Distance in kilometers
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distance) => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m away`
  }
  return `${distance.toFixed(1)}km away`
}

/**
 * Debounce function to limit API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function for scroll events etc
 * @param {Function} func - Function to throttle
 * @param {number} limit - Delay in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle
  return function () {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * Generate star rating display
 * @param {number} rating - Rating value (0-5)
 * @returns {Object} Star rating breakdown
 */
export const generateStarRating = (rating) => {
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5
  const emptyStars = 5 - Math.ceil(rating)

  return {
    fullStars,
    hasHalfStar,
    emptyStars,
    rating: rating.toFixed(1)
  }
}

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate South African phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Is valid phone number
 */
export const validatePhoneNumber = (phone) => {
  // South African phone number patterns
  const phoneRegex = /^(\+27|0)[0-9]{9}$/
  return phoneRegex.test(phone.replace(/\s/g, ''))
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '')

  if (cleaned.startsWith('27')) {
    return `+27 ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`
  }

  if (cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }

  return phone
}

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param {Date|string} date - Date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  const now = new Date()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const diffInSeconds = Math.floor((now - dateObj) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`

  return formatDate(dateObj)
}

/**
 * Capitalize first letter of each word
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const titleCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Generate initials from a name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

/**
 * Check if time slot is available today
 * @param {string} time - Time in HH:MM format
 * @returns {boolean} Is time slot still available today
 */
export const isTimeSlotAvailable = (time) => {
  const now = new Date()
  const [hours, minutes] = time.split(':')
  const slotTime = new Date()
  slotTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)

  // Add 30 minutes buffer for booking
  const bufferTime = new Date(now.getTime() + 30 * 60000)

  return slotTime > bufferTime
}

/**
 * Sort doctors by various criteria
 * @param {Array} doctors - Array of doctor objects
 * @param {string} sortBy - Sort criteria (rating, distance, availability, name)
 * @param {string} order - Sort order (asc, desc)
 * @returns {Array} Sorted doctors array
 */
export const sortDoctors = (doctors, sortBy = 'rating', order = 'desc') => {
  return [...doctors].sort((a, b) => {
    let aValue, bValue

    switch (sortBy) {
      case 'rating':
        aValue = a.rating || 0
        bValue = b.rating || 0
        break
      case 'distance':
        aValue = a.distance || Infinity
        bValue = b.distance || Infinity
        break
      case 'name':
        aValue = a.name.toLowerCase()
        bValue = b.name.toLowerCase()
        break
      case 'reviewCount':
        aValue = a.reviewCount || 0
        bValue = b.reviewCount || 0
        break
      default:
        return 0
    }

    if (order === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
    }
  })
}

/**
 * Filter doctors based on search criteria
 * @param {Array} doctors - Array of doctor objects
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered doctors array
 */
export const filterDoctors = (doctors, filters) => {
  return doctors.filter((doctor) => {
    // Specialty filter
    if (filters.specialty && doctor.specialty !== filters.specialty) {
      return false
    }

    // Rating filter
    if (filters.minRating && doctor.rating < parseFloat(filters.minRating)) {
      return false
    }

    // Language filter
    if (filters.languages && filters.languages.length > 0) {
      const hasLanguage = filters.languages.some((lang) => doctor.languages && doctor.languages.includes(lang))
      if (!hasLanguage) return false
    }

    // Medical aid filter
    if (filters.medicalAids && filters.medicalAids.length > 0) {
      const acceptsMedicalAid = filters.medicalAids.some(
        (aid) => doctor.medicalAids && doctor.medicalAids.includes(aid)
      )
      if (!acceptsMedicalAid) return false
    }

    // Gender filter
    if (filters.gender && filters.gender !== 'any' && doctor.gender !== filters.gender) {
      return false
    }

    return true
  })
}

/**
 * Get South African time (UTC+2)
 * @param {Date} date - Date to convert
 * @returns {Date} South African time
 */
export const getSouthAfricanTime = (date = new Date()) => {
  const utc = date.getTime() + date.getTimezoneOffset() * 60000
  return new Date(utc + 2 * 3600000) // UTC+2
}

/**
 * Format time slot for display
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time slot string
 */
export const formatTimeSlot = (time) => {
  if (!time) return ''

  try {
    const [hours, minutes] = time.split(':')
    const date = new Date()
    date.setHours(parseInt(hours), parseInt(minutes))

    return date.toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  } catch (error) {
    return time
  }
}

/**
 * Check if a time slot is in the past
 * @param {Date} date - Date to compare
 * @param {string} time - Time in HH:MM format
 * @returns {boolean} Is time slot in the past
 */
export const isTimeSlotPast = (date, time) => {
  if (!date || !time) return false

  try {
    const [hours, minutes] = time.split(':')
    const slotDateTime = new Date(date)
    slotDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)

    const now = getSouthAfricanTime()
    return slotDateTime <= now
  } catch (error) {
    return false
  }
}

/**
 * Generate time slots for a day (used in availability)
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @param {number} interval - Time interval in minutes
 * @returns {Array} Array of time slots
 */
export const generateTimeSlots = (startTime = '08:00', endTime = '17:00', interval = 15) => {
  const slots = []
  const start = new Date(`2000-01-01T${startTime}:00`)
  const end = new Date(`2000-01-01T${endTime}:00`)

  while (start < end) {
    slots.push(start.toTimeString().slice(0, 5))
    start.setMinutes(start.getMinutes() + interval)
  }

  return slots
}

/**
 * Generate unique ID
 * @returns {string} Unique ID
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * Deep clone object
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime())
  if (obj instanceof Array) return obj.map((item) => deepClone(item))
  if (typeof obj === 'object') {
    const cloned = {}
    Object.keys(obj).forEach((key) => {
      cloned[key] = deepClone(obj[key])
    })
    return cloned
  }
}

/**
 * Capitalize first letter
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Format currency (South African Rand)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'ZAR') => {
  if (amount === null || amount === undefined) return ''

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount)
}

/**
 * Get weekday name
 * @param {Date} date - Date to get weekday name
 * @param {string} format - Format type (long, short)
 * @returns {string} Weekday name
 */
export const getWeekdayName = (date, format = 'long') => {
  if (!date) return ''

  const d = new Date(date)
  return d.toLocaleDateString('en-ZA', { weekday: format })
}

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} Are dates the same day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false

  const d1 = new Date(date1)
  const d2 = new Date(date2)

  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

/**
 * Get week start (Monday)
 * @param {Date} date - Date to get week start
 * @returns {Date} Monday date
 */
export const getWeekStart = (date = new Date()) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
  return new Date(d.setDate(diff))
}

/**
 * Highlight search term in text
 * @param {string} text - Text to highlight term in
 * @param {string} searchTerm - Term to highlight
 * @returns {string} Highlighted text
 */
export const highlightSearchTerm = (text, searchTerm) => {
  if (!text || !searchTerm) return text

  const regex = new RegExp(`(${searchTerm})`, 'gi')
  return text.replace(regex, '<mark>$1</mark>')
}

/**
 * Group array items by key
 * @param {Array} array - Array of items
 * @param {string} key - Key to group by
 * @returns {Object} Grouped items
 */
export const groupBy = (array, key) => {
  return array.reduce((grouped, item) => {
    const group = item[key]
    if (!grouped[group]) {
      grouped[group] = []
    }
    grouped[group].push(item)
    return grouped
  }, {})
}

/**
 * Sort array by key
 * @param {Array} array - Array of items
 * @param {string} key - Key to sort by
 * @param {string} direction - Sort direction (asc, desc)
 * @returns {Array} Sorted array
 */
export const sortBy = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]

    if (direction === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })
}

/**
 * Set local storage item
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} Success status
 */
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    console.error('Error saving to localStorage:', error)
    return false
  }
}

/**
 * Get local storage item
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key not found
 * @returns {any} Stored value
 */
export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error('Error reading from localStorage:', error)
    return defaultValue
  }
}

/**
 * Remove local storage item
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.error('Error removing from localStorage:', error)
    return false
  }
}
